import { useEffect, useState } from 'react';
import { hasEligiblePendingSyncWork } from '@/services/syncService';
import { getClientInstallationPresenceMetadata } from '@/services/runtime/clientInstallationPresence';
import { getStableRecoveryDeviceId } from '@/services/recovery/RecoveryRuntimeGate';
import {
  postUpdateCollectionStore,
  type PostUpdatePresentation,
} from '@/services/runtime/postUpdateCollectionStore';

export const describeSupportReceipts = (value: PostUpdatePresentation | null): string | null => {
  if (!value) return null;
  const diagnostic = value.stages.diagnostic.state === 'received' ? 'recebido pelo suporte' : 'recebimento ainda não confirmado';
  const fullState = value.stages.full_state.state === 'received' ? 'recebidos pelo suporte' : 'recebimento ainda não confirmado';
  return `Diagnóstico: ${diagnostic}.\nDados completos: ${fullState}.`;
};

export const describePendingSends = (online: boolean, pending: boolean | null, lastStatus: string): string | null => {
  if (!online) return pending
    ? 'Há envios pendentes. Conecte-se à internet para continuar.'
    : 'Sem conexão. Os envios precisam de internet.';
  if (lastStatus === 'partial' || lastStatus === 'error') return 'A sincronização ficou incompleta. Os dados pendentes foram mantidos para nova tentativa.';
  return pending ? 'Há envios pendentes. A sincronização continua automaticamente.' : null;
};

// Apenas observa os fluxos existentes; não dispara sync nem cria pedidos de suporte.
export function useHomeSyncStatus({ userId, companyId, ready, online, busy, lastStatus, lastSyncDate }: {
  userId: string | null | undefined;
  companyId: string | null | undefined;
  ready: boolean;
  online: boolean;
  busy: boolean;
  lastStatus: string;
  lastSyncDate: unknown;
}) {
  const scope = `${companyId ?? ''}:${userId ?? ''}`;
  const [pending, setPending] = useState<{ scope: string; value: boolean | null } | null>(null);
  const [, refreshReceipts] = useState(0);

  useEffect(() => postUpdateCollectionStore.subscribePresentation(() => refreshReceipts(value => value + 1)), []);

  useEffect(() => {
    let active = true;
    if (!ready || !userId || !companyId || busy) return;
    setPending({ scope, value: null });
    void hasEligiblePendingSyncWork(companyId, userId).then(
      value => { if (active) setPending({ scope, value }); },
      () => { if (active) setPending({ scope, value: null }); }
    );
    return () => { active = false; };
  }, [scope, companyId, userId, ready, online, busy, lastSyncDate]);

  let presentation: PostUpdatePresentation | null = null;
  try {
    const metadata = getClientInstallationPresenceMetadata();
    const build = metadata?.platform === 'web' ? metadata.compatibilityBuild : metadata?.nativeBuild;
    if (ready && userId && companyId && metadata && build) {
      presentation = postUpdateCollectionStore.getPresentation({
        userId, companyId, platform: metadata.platform, deviceId: getStableRecoveryDeviceId(),
      }, build);
    }
  } catch { /* Uma falha no aviso não deve bloquear a Home. */ }

  return {
    pendingMessage: ready ? describePendingSends(online, pending?.scope === scope ? pending.value : null, lastStatus) : null,
    receiptMessage: describeSupportReceipts(presentation),
  };
}
